import { IOgqlJobResult, IOgqlV1JobResult, IOgqlView, IOgqlViewDetail } from '@opengov/ogql-types';
import {
  ICurrentOrLegacyReport,
  IGenerateOgqlQueryResponse,
  IGrantee,
  IHydrationContext,
  IInvitableUser,
  IReport,
  IReportAndSavedViewData,
  IReportColumnValues,
  IReportConfiguration,
  IReportConfigurationPayload,
  IReportConfigurationResponse,
  IReportDatasetLens,
  IReportResponse,
  ISavedView,
  ISavedViewResponse,
  ISummaryTableJobResult,
  ITemplateReadable,
  IValidateReportResponse
} from '@opengov/reporting-types';

import { baseEmbedServiceUrl, baseOgqlServiceUrl, baseReportsV2ServiceUrl, baseTemplateServiceUrl } from './paths';
import { getServiceURL, platform } from './transportWrapper';
import { IEntity } from './types';
import { IRecordOf } from '@opengov/compose-app/immutable';

const reportsControllerUrl = `${baseReportsV2ServiceUrl}/reports`;
const templatesControllerUrl = `${baseTemplateServiceUrl}/templates`;
const viewControllerUrl = `${baseOgqlServiceUrl}/views`;
export const generateQueriesApiUrl = `${baseReportsV2ServiceUrl}/generateOgqlQueries`;

export const getViews = async (
  params: { entityId: number; limit?: number; offset?: number },
  clientOptions?: RequestInit,
  hostUrl?: string
): Promise<IOgqlView[]> => {
  let getViewsUrl = `${viewControllerUrl}?entity_id=${params.entityId}`;
  const limit = params.limit !== undefined ? params.limit : 1000;
  getViewsUrl += `&limit=${limit}`;
  if (params.offset !== undefined) {
    getViewsUrl += `&offset=${params.offset}`;
  }
  return getServiceURL(hostUrl)
    .options(clientOptions, true)
    .url(getViewsUrl)
    .get()
    .json()
    .then((payload) => {
      return payload.views;
    })
    .catch(async (e) => {
      e.message = `reportV2GetViews: ${e.message}`;
      return Promise.reject(e);
    });
};

export const describeView = async (
  params: { viewId: string },
  clientOptions?: RequestInit
): Promise<IOgqlViewDetail> => {
  return platform
    .options(clientOptions, true)
    .url(`${viewControllerUrl}/${params.viewId}`)
    .get()
    .json<IOgqlViewDetail>()
    .catch(async (e) => {
      e.message = `reportV2DescribeView: ${e.message}`;
      return Promise.reject(e);
    });
};

export const reportsByEntityId = async (
  params: { entityId: number },
  clientOptions?: RequestInit
): Promise<IReport[]> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports?entityId=${params.entityId}`)
    .get()
    .json<IReport[]>()
    .catch(async (e) => {
      e.message = `reportsV2ByEntityId: ${e.message}`;
      return Promise.reject(e);
    });

export const reportOgqlView = async (params: { reportId: string }, clientOptions?: RequestInit): Promise<IOgqlView> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${params.reportId}/ogql-view`)
    .get()
    .json<IOgqlView>();

export const getEntityByReportId = async (
  params: { reportId: string },
  clientOptions?: RequestInit
): Promise<IEntity> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${params.reportId}/entity`)
    .get()
    .json<IEntity>();

export interface IReportStandardQuery {
  templateId: string;
  limit?: number;
  offset?: number;
  queryType: string;
  body: object;
  useLargeScaleQueryLimit?: boolean;
  useQueryService?: boolean;
}

// IOgqlV1JobResult or IOgqlJobResult
export const reportStandardQuery = async <R>(params: IReportStandardQuery, clientOptions?: RequestInit): Promise<R> => {
  const { limit, offset, queryType, body, templateId, useLargeScaleQueryLimit, useQueryService } = params;
  let makeQuery = platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/templates/${templateId}/queries/${queryType}`)
    .query({
      useLargeScaleQueryLimit,
      useQueryService
    });
  if (offset) {
    makeQuery = makeQuery.query({ offset });
  }
  if (limit) {
    makeQuery = makeQuery.query({ limit });
  }
  return makeQuery.json(body).post().json<R>();
};

export const reportOgqlV1Query = async (
  params: {
    queryType: string;
    reportConfiguration: IReportConfiguration;
    useLargeScaleQueryLimit?: boolean;
    useQueryService?: boolean;
    limit?: number;
    offset?: number;
    dimensionalSummaryQueryIndex?: number;
  },
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<IOgqlV1JobResult> => {
  const {
    queryType,
    reportConfiguration,
    useLargeScaleQueryLimit,
    useQueryService,
    limit,
    offset,
    dimensionalSummaryQueryIndex
  } = params;

  let makeQuery = getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${reportConfiguration.reportId}/queries/${queryType}`)
    .query({
      useLargeScaleQueryLimit,
      useQueryService,
      ...(dimensionalSummaryQueryIndex >= 0 && { dimensionalSummaryQueryIndex })
    });
  if (offset) {
    makeQuery = makeQuery.query({ offset });
  }
  if (limit) {
    makeQuery = makeQuery.query({ limit });
  }
  return makeQuery.json(reportConfiguration).post().json<IOgqlV1JobResult>();
};

export const reportOgqlQuery = async (
  params: {
    reportId: string;
    queryId?: string;
    queryType: string;
    body: object;
    useLargeScaleQueryLimit?: boolean;
    useQueryService?: boolean;
    limit?: number;
    offset?: number;
  },
  clientOptions?: RequestInit
): Promise<IOgqlJobResult> => {
  const { queryType, reportId, body, useLargeScaleQueryLimit, useQueryService, limit, offset } = params;
  let makeQuery = platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${reportId}/queries/${queryType}`)
    .query({
      useLargeScaleQueryLimit,
      useQueryService
    });

  if (offset) {
    makeQuery = makeQuery.query({ offset });
  }
  if (params.limit) {
    makeQuery = makeQuery.query({ limit });
  }
  return makeQuery.json(body).post().json<IOgqlJobResult>();
};

export const currentAndLegacyReportsByEntityId = async (
  params: {
    entityId: number;
    includeAccountSummaryReports?: boolean;
    includeMultiDatasetReports?: boolean;
  },
  clientOptions?: RequestInit
): Promise<ICurrentOrLegacyReport[]> =>
  platform
    .options(clientOptions, true)
    .url(
      // tslint:disable-next-line
      `${baseReportsV2ServiceUrl}/currentAndLegacyReports?entityId=${params.entityId}&includeAccountSummaryReports=${params.includeAccountSummaryReports}&includeMultiDatasetReports=${params.includeMultiDatasetReports}`
    )
    .get()
    .json<ICurrentOrLegacyReport[]>()
    .catch(async (e) => {
      e.message = `reportsV2ByEntityId: ${e.message}`;
      return Promise.reject(e);
    });

export const getSummaryTableDataForNextGenReport = async (
  params: {
    savedViewId: string;
  },
  clientOptions?: RequestInit,
  host?: string
): Promise<ISummaryTableJobResult> => {
  return getServiceURL(host)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/summaryTable`)
    .json(params)
    .post()
    .json<ISummaryTableJobResult>()
    .catch(async (e) => {
      e.message = `error in summary table data reports: ${e}`;
      return Promise.reject(e);
    });
};

export const getDetailTableDataReport = async (
  params: {
    savedViewId: string;
  },
  clientOptions?: RequestInit,
  host?: string
): Promise<ISummaryTableJobResult> => {
  return getServiceURL(host)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/detailsTable`)
    .json(params)
    .post()
    .json<ISummaryTableJobResult>()
    .catch(async (e) => {
      e.message = `error in detail table data reports: ${e.message}`;
      return Promise.reject(e);
    });
};

export const getSummaryTableDataForClassicReport = async (
  params: {
    savedViewId: string;
    displayAccountCodes: boolean;
    isEmbedRebuild?: boolean;
  },
  clientOptions?: RequestInit,
  host?: string
): Promise<any> => {
  return getServiceURL(host)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/classicReports/summaryTable`)
    .json(params)
    .post()
    .json<any>()
    .catch(async (e) => {
      e.message = `error in classic summary table data reports: ${e}`;
      return Promise.reject(e);
    });
};

export const reportById = async (
  params: { reportId: string },
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<IReport> => {
  return getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${params.reportId}`)
    .get()
    .json<IReport>()
    .catch(async (e) => {
      e.message = `reportV2ReportById: ${e.message}`;
      return Promise.reject(e);
    });
};

export const reportDataByIds = async (
  params: { savedViewIds: string[] },
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<IReportAndSavedViewData> => {
  const { savedViewIds } = params;
  return getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/reportDataByIds`)
    .post({ savedViewIds })
    .json<IReportAndSavedViewData>();
};

export const deleteReportById = async (params: { reportId: string }, clientOptions?: RequestInit): Promise<any> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${params.reportId}`)
    .delete()
    .res()
    .catch(async (e) => {
      e.message = `reportV2DeleteReportById: ${e.message}, reportId: ${params.reportId}`;
      return Promise.reject(e);
    });

export const validateReportDataset = async (
  reportId: string,
  datasetId: string,
  clientOptions?: RequestInit
): Promise<IValidateReportResponse> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${reportId}/validate-dataset`)
    .post({ datasetId })
    .json<IValidateReportResponse>()
    .catch(async (e) => {
      e.message = `validateReportDataset: ${e.message}, reportId: ${reportId}`;
      return Promise.reject(e);
    });

export const getAllReportsByEntityId = async (entityId: string, clientOptions?: RequestInit): Promise<any> => {
  return platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports?entityId=${entityId}`)
    .get()
    .json<IReport>()
    .catch(async (e) => {
      e.message = `getAllReportUnderEntity: ${e.message}, entityId: ${entityId}`;
      return Promise.reject(e);
    });
};

export const savedViewsByEntityId = async (
  params: { entityId: number },
  clientOptions?: RequestInit
): Promise<ISavedViewResponse[]> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/savedViews?entityId=${params.entityId}`)
    .get()
    .json<ISavedViewResponse[]>()
    .catch(async (e) => {
      e.message = `savedViewsV2ByEntityId: ${e.message}`;
      return Promise.reject(e);
    });

export const savedViewsByReportId = async (
  params: { reportId: string },
  clientOptions?: RequestInit
): Promise<ISavedViewResponse[]> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${params.reportId}/savedViews`)
    .get()
    .json<ISavedViewResponse[]>()
    .catch(async (e) => {
      e.message = `savedViewsV2ByReportId: ${e.message}`;
      return Promise.reject(e);
    });

export const savedViewById = async (
  params: { savedViewId: string },
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<ISavedViewResponse> =>
  getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/savedViews/${params.savedViewId}`)
    .get()
    .json<ISavedViewResponse>()
    .catch(async (e) => {
      e.message = `savedViewV2ById: ${e.message}`;
      return Promise.reject(e);
    });

export const createReportFromViewId = async (
  params: {
    entityId: number;
    viewId: string;
    lensId?: string;
    title: string;
    description?: string;
    isDimensional?: boolean;
    isMultiDataset?: boolean;
  },
  clientOptions?: RequestInit
): Promise<IReport> => {
  return platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports`)
    .json({
      entityId: params.entityId,
      viewId: params.viewId,
      lensId: params.lensId || null,
      title: params.title,
      description: params.description,
      isDimensional: params.isDimensional || false,
      isMultiDataset: params.isMultiDataset || false,
      defaultReportConfiguration: null
    })
    .post()
    .json<IReport>()
    .catch(async (e) => {
      e.message = `reportV2CreateReportFromViewId: ${e.message}`;
      return Promise.reject(e);
    });
};

export const createReportConfiguration = async (
  reportConfiguration: IReportConfiguration,
  clientOptions?: RequestInit
): Promise<IReportConfigurationResponse> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reportConfigurations`)
    .json(reportConfiguration)
    .post()
    .json<IReportConfigurationResponse>();

export const createColumnGroup = async (
  params: { reportConfiguration: IReportConfiguration; datasetId: string },
  clientOptions?: RequestInit
): Promise<IReportConfigurationResponse> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reportConfigurations/accountSummary/columnGroup`)
    .json(params)
    .post()
    .json<IReportConfigurationResponse>();

export const getReportConfigurationById = async (
  reportConfigurationId: string,
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<IReportConfigurationResponse> =>
  getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reportConfigurations/${reportConfigurationId}`)
    .get()
    .json<IReportConfigurationResponse>();

export const getOgqlQueries = async (
  {
    reportConfiguration,
    useLargeScaleQueryLimit = false,
    isExporting = false
  }: {
    reportConfiguration: IReportConfigurationPayload;
    useLargeScaleQueryLimit?: boolean;
    isExporting?: boolean;
  },
  clientOptions?: RequestInit
): Promise<IGenerateOgqlQueryResponse> => {
  return platform
    .options(clientOptions, true)
    .query({ useLargeScaleQueryLimit, isExporting })
    .url(generateQueriesApiUrl)
    .json(reportConfiguration)
    .post()
    .json<IGenerateOgqlQueryResponse>();
};

export const updateSavedViewById = async (
  params: {
    savedViewId: string;
    newSavedView: Partial<ISavedView> & { setAsDefault?: boolean };
    updateSiblingViews?: boolean;
  },
  clientOptions?: RequestInit
): Promise<ISavedViewResponse> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/savedViews/${params.savedViewId}`)
    .query({ updateSiblingViews: params.updateSiblingViews ?? false })
    .json(params.newSavedView)
    .put()
    .json<ISavedViewResponse>()
    .catch(async (e) => {
      e.message = `savedViewByIdV2: ${e.message}`;
      return Promise.reject(e);
    });

export const updateSavedViewsByReportId = async (
  params: {
    report: IRecordOf<IReportResponse>;
    reportConfig: IReportConfiguration;
    savedViewId?: string;
    entityLenses?: IReportDatasetLens[];
    fiscalYearStartMonth?: number;
  },
  clientOptions?: RequestInit
): Promise<[IReport, ...ISavedView[]]> =>
  platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${params.report.id}/savedViews`)
    .put(params)
    .json<[IReport, ...ISavedView[]]>();

export const deleteSavedViewById = async (
  params: { savedViewId: string },
  clientOptions?: RequestInit
): Promise<any> => {
  return platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/savedViews/${params.savedViewId}`)
    .delete()
    .res()
    .catch(async (e) => {
      e.message = `deleteSavedViewByIdV2: ${e.message}`;
      return Promise.reject(e);
    });
};

export const createSavedView = async (
  params: {
    entityId: number;
    reportId: string;
    title: string;
    reportConfiguration: string;
    description?: string;
    setAsDefault?: boolean;
  },
  clientOptions?: RequestInit
): Promise<ISavedViewResponse> => {
  return platform
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/savedViews`)
    .json(params)
    .post()
    .json<ISavedViewResponse>()
    .catch(async (e) => {
      e.message = `createSavedViewV2: ${e.message}`;
      throw e;
    });
};

export const createSavedViewPreview = async (
  params: {
    title: string;
    description: string;
    reportId: string;
    savedViewId: string;
  },
  clientOptions?: RequestInit,
  hostUrl?: string
): Promise<any> => {
  const payload = {
    title: params.title,
    description: params.description,
    report_id: params.reportId,
    report_version: 'v2',
    saved_view_id: params.savedViewId,
    should_sync: true
  };
  return getServiceURL(hostUrl)
    .options(clientOptions, true)
    .url(`${baseEmbedServiceUrl}/embeds`)
    .json(payload)
    .post()
    .json<any>()
    .catch(async (e) => {
      e.message = `createSavedViewPreviewV2: ${e.message}`;
      return Promise.reject(e);
    });
};

export const updateSavedViewPreview = async (
  params: { embedId: string; title: string; description: string },
  clientOptions?: RequestInit,
  hostUrl?: string
): Promise<any> => {
  return getServiceURL(hostUrl)
    .options(clientOptions, true)
    .url(`${baseEmbedServiceUrl}/embeds/${params.embedId}`)
    .json({ title: params.title, description: params.description, should_sync: true })
    .put()
    .json<any>()
    .catch(async (e) => {
      e.message = `updateSavedViewPreviewV2: ${e.message}`;
      return Promise.reject(e);
    });
};

export const getReportLenses = async (reportId: string, clientOptions?: RequestInit, reportingServiceHost?: string) =>
  getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${reportId}/lenses`)
    .get()
    .json()
    .then((lenses: IReportDatasetLens[]): IReportDatasetLens[] => lenses);

export const getStandardReportLens = async (
  lensId: string,
  entityUuid: string,
  reportConfigurationId?: string,
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<string | IReportDatasetLens> => {
  const queryParams: { entityId; reportConfigurationId?} = { entityId: entityUuid };

  if (reportConfigurationId) {
    queryParams.reportConfigurationId = reportConfigurationId;
  }

  return getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/lenses/${encodeURIComponent(lensId)}`)
    .query(queryParams)
    .get()
    .json()
    .catch(() => `Error with lens`)
    .then((lens: IReportDatasetLens): IReportDatasetLens => lens);
};

export const reportingRequests = {
  getOgqlQueries
};

export const getReportInvitableUsers = async (
  params: {
    reportId: string;
  },
  clientOptions?: RequestInit
): Promise<IInvitableUser[]> =>
  platform
    .options(clientOptions, true)
    .url(`${reportsControllerUrl}/${params.reportId}/invitableUsers`)
    .get()
    .json<IInvitableUser[]>()
    .catch(async (e) => {
      e.message = `reportV2ReportById: ${e.message}`;
      return Promise.reject(e);
    });

export const getReportGrants = async (
  params: {
    reportId: string;
  },
  clientOptions?: RequestInit
): Promise<IGrantee[]> =>
  platform
    .options(clientOptions, true)
    .url(`${reportsControllerUrl}/${params.reportId}/grants`)
    .get()
    .json<IGrantee[]>()
    .catch(async (e) => {
      e.message = `Error getting report grants of report: ${params.reportId}. Error: ${e.message}`;
      return Promise.reject(e);
    });

export const updateReportGrants = async (
  params: {
    reportId: string;
    grantees: IGrantee[];
  },
  clientOptions?: RequestInit
) =>
  platform
    .options(clientOptions, true)
    .url(`${reportsControllerUrl}/${params.reportId}/grants`)
    .json(params.grantees)
    .put()
    .json()
    .catch(async (e) => {
      e.message = `reportV2ReportById: ${e.message}`;
      return Promise.reject(e);
    });

export const getStandardTemplates = async (
  params: { requestedEntitySubdomain: string },
  clientOptions?: RequestInit,
  hostUrl?: string
): Promise<any> => {
  return getServiceURL(hostUrl)
    .options(clientOptions, true)
    .query({ requestedEntitySubdomain: params.requestedEntitySubdomain })
    .url(templatesControllerUrl)
    .get()
    .json<ITemplateReadable[]>()
    .catch(async (e) => {
      e.message = `Error getting Standard Report Templates. Error: ${e.message}`;
      return Promise.reject(e);
    });
};

export const getStandardTemplate = async (
  params: { requestedEntitySubdomain: string; templateId: string },
  clientOptions?: RequestInit,
  hostUrl?: string
): Promise<any> => {
  return getServiceURL(hostUrl)
    .options(clientOptions, true)
    .query({ requestedEntitySubdomain: params.requestedEntitySubdomain })
    .url(`${templatesControllerUrl}/${params.templateId}`)
    .get()
    .json<ITemplateReadable>()
    .catch(async (e) => {
      e.message = `Error getting Standard Report Template. Error: ${e.message}`;
      return Promise.reject(e);
    });
};

export const hydrateStandardTemplate = async (
  params: { requestedEntitySubdomain: string; templateId: string },
  body: IHydrationContext,
  clientOptions?: RequestInit,
  hostUrl?: string
): Promise<any> => {
  // we don't want templateId in the queryParams
  return getServiceURL(hostUrl)
    .options(clientOptions, true)
    .query({ requestedEntitySubdomain: params.requestedEntitySubdomain })
    .url(`${templatesControllerUrl}/${params.templateId}/hydrate`)
    .json(body)
    .post()
    .json<ITemplateReadable>()
    .catch(async (e) => {
      e.message = `Error hydrating Standard Report Template. Error: ${e.message}`;
      return Promise.reject(e);
    });
};

export const getFilterColumnValues = async (
  params?: { reportId: string; relation: string; column: string; type: string; filter?: string },
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<IReportColumnValues> => {
  const filterParam = params.filter ? `?filter=${params.filter}` : '';

  return getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${reportsControllerUrl}/${params.reportId}`)
    .url(`/relations/${params.relation}`)
    .url(`/columns/${params.column}`)
    .url(`/columnType/${params.type}`)
    .url(filterParam)
    .get()
    .json()
    .then((columnValues: IReportColumnValues): IReportColumnValues => columnValues);
};
